import { isTestnet } from "./registry";
import type { AssetBalance, AssetsResponse } from "./types";

type PortfolioChainBalance = {
  chainId?: number;
  chain?: number;
  address?: string;
  token?: string;
  balance?: string;
  amount?: string;
  decimals?: number;
  isTestnet?: boolean;
};

type PortfolioAsset = {
  symbol?: string;
  decimals?: number;
  balance?: string;
  amount?: string;
  address?: string;
  token?: string;
  usdValue?: number;
  chains?: PortfolioChainBalance[];
};

type FetchAssetsResponseOptions = {
  providerUrl: string;
  identifier: string;
  clientId?: string;
  accessToken?: string;
  headers?: Record<string, string>;
};

/**
 * Mark a normalized portfolio row with a stable network bucket flag.
 */
function withNetworkBucket(balance: AssetBalance): AssetBalance {
  return {
    ...balance,
    isTestnet: balance.isTestnet ?? isTestnet(balance.chainId),
  };
}

/**
 * Split normalized balances into explicit mainnet and testnet buckets.
 */
function bucketAssetBalances(balances: AssetBalance[]): Pick<
  AssetsResponse,
  "mainnets" | "testnets"
> {
  const mainnetBalances: AssetBalance[] = [];
  const testnetBalances: AssetBalance[] = [];

  for (const balance of balances) {
    const normalized = withNetworkBucket(balance);
    if (normalized.isTestnet) {
      testnetBalances.push(normalized);
    } else {
      mainnetBalances.push(normalized);
    }
  }

  return {
    mainnets: { balances: mainnetBalances },
    testnets: { balances: testnetBalances },
  };
}

/**
 * Convert passkey portfolio responses into the documented assets response.
 */
export function normalizeAssetsResponse(payload: unknown): AssetsResponse {
  const data = payload && typeof payload === "object"
    ? (payload as Record<string, unknown>)
    : {};
  if (Array.isArray(data.balances)) {
    const balances = (data.balances as AssetBalance[]).map(withNetworkBucket);
    const buckets = bucketAssetBalances(balances);
    return { ...data, balances, ...buckets };
  }

  const balances: AssetBalance[] = [];
  const assets = Array.isArray(data.assets) ? (data.assets as PortfolioAsset[]) : [];
  for (const asset of assets) {
    const symbol = asset.symbol ?? "UNKNOWN";
    const decimals = asset.decimals ?? 18;
    const chains = Array.isArray(asset.chains) ? asset.chains : [];

    if (chains.length === 0) {
      const token = asset.token ?? asset.address;
      if (token) {
        balances.push(withNetworkBucket({
          chainId: 0,
          token,
          symbol,
          decimals,
          balance: asset.balance ?? asset.amount ?? "0",
          ...(asset.usdValue !== undefined && { usdValue: asset.usdValue }),
        }));
      }
      continue;
    }

    for (const chain of chains) {
      const chainId = chain.chainId ?? chain.chain;
      const token = chain.token ?? chain.address;
      if (typeof chainId !== "number" || !token) continue;
      balances.push(withNetworkBucket({
        chainId,
        token,
        symbol,
        decimals: chain.decimals ?? decimals,
        balance: chain.balance ?? chain.amount ?? "0",
        ...(asset.usdValue !== undefined && { usdValue: asset.usdValue }),
        ...(chain.isTestnet !== undefined && { isTestnet: chain.isTestnet }),
      }));
    }
  }

  return { ...data, balances, ...bucketAssetBalances(balances) };
}

/**
 * Fetch the unified passkey portfolio and return normalized wallet assets.
 */
export async function fetchAssetsResponse(
  options: FetchAssetsResponseOptions,
): Promise<AssetsResponse> {
  const portfolioUrl = new URL(
    `/api/users/${encodeURIComponent(options.identifier)}/portfolio`,
    options.providerUrl,
  );
  // `all=true` keeps the SDK from silently hiding testnet balances when the
  // caller is using a mixed mainnet/testnet account during dogfooding.
  portfolioUrl.searchParams.set("all", "true");

  const response = await fetch(portfolioUrl.toString(), {
    headers: {
      ...options.headers,
      ...(options.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : {}),
      ...(options.clientId ? { "x-client-id": options.clientId } : {}),
    },
  });
  if (!response.ok) {
    const data = await response.json().catch(() => ({}));
    throw new Error(data.error || "Failed to get assets");
  }
  return normalizeAssetsResponse(await response.json());
}
